export enum MatchType {
  INCLUDES = 'includes',
  EXACT = 'exact'
}

export type ConfigPattern = {
  matchType: MatchType
  value: string
}

export const configPatterns: readonly ConfigPattern[] = [
  // EXACT patterns
  { matchType: MatchType.EXACT, value: '/smart-rebalance' },
  { matchType: MatchType.EXACT, value: '/peer-analyser' },
  { matchType: MatchType.EXACT, value: '/mutual-fund/:isin' },
  { matchType: MatchType.EXACT, value: '/elite/:variant' },
  { matchType: MatchType.EXACT, value: '/regular-to-direct-plan' },
  { matchType: MatchType.EXACT, value: '/regular-to-direct-plan-elite' },
  { matchType: MatchType.EXACT, value: '/tax-harvesting' },
  // INCLUDES patterns
  { matchType: MatchType.INCLUDES, value: 'elite-dashboard' },
  { matchType: MatchType.INCLUDES, value: 'broke-landing-page' },
  { matchType: MatchType.INCLUDES, value: 'elite' }
]

/**
 * Usage guide:
 * - Keep all route rules in `configPatterns`.
 * - Use `MatchType.EXACT` for route-pattern matching (supports static routes like
 *   `/peer-analyser` and param routes like `/mutual-fund/:isin`).
 * - Use `MatchType.INCLUDES` for substring checks when multiple related paths
 *   should share the same behavior.
 *
 * This function is the single entry point used by routing code.
 * It returns the bridge-friendly flag:
 * - `true`  => hardware back should be blocked
 * - `false` => hardware back should be allowed
 */
export function shouldBlockHardwareBack(pathname: string): boolean {
  if (!pathname) return true
  return !isHardwareBackAllowedPathname(pathname)
}

/* 
========================================
HELPER FUNCTIONS 
========================================
*/
export function matchesRoutePattern(pathname: string, pattern: string): boolean {
  const pathnameSegments = pathname?.split('/')?.filter(Boolean)
  const configPatternSegments = pattern?.split('/')?.filter(Boolean)

  if (pathnameSegments?.length !== configPatternSegments?.length) return false

  // Check if the pathname matches the pattern
  return configPatternSegments?.every((configPatternSegment, index) => {
    const pathnameSegment = pathnameSegments?.[index] ?? ''
    if (configPatternSegment?.startsWith(':')) return Boolean(pathnameSegment)
    return configPatternSegment === pathnameSegment
  })
}

export function isHardwareBackAllowedPathname(pathname: string): boolean {
  return configPatterns?.some((patternConfig) => {
    if (patternConfig?.matchType === MatchType.INCLUDES) {
      return pathname?.includes(patternConfig?.value ?? '')
    }
    return matchesRoutePattern(pathname, patternConfig?.value ?? '')
  })
}
